'use client';

import { BoxScrolled, usePortalContext } from '@cloai/uikit';
import _ from 'lodash';
import { useTranslations } from 'next-intl';
import { SyntheticEvent, FC, useMemo, memo } from 'react';

import { PORTALS } from '@/.constants/portals';
import { StyleCategory } from '@/.gql/graphql';
import AiVideosIcon from '@/public/icons/ai_videos.svg';
import BestIcon from '@/public/icons/star.svg';
import { SeeAllButton } from '@/src/components/SeeAllButton';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import {
  useProfileContext,
  useStyleContext,
  useGTMContext,
  TActualItem,
} from '@/src/providers';
import { getBlockName } from '@/src/utils/getBlockName';
import { getImageByGender } from '@/src/utils/getImageByGender';

import { PackPreview } from './PackPreview';

const styles = {
  wrapper: 'flex flex-col gap-3',
  line: 'flex items-center justify-between',
  lineInner: 'flex items-center gap-2',
  lineTitle: 'text-lg font-bold',
  lineIcon: 'w-5 h-5',
  lineCount: 'text-sm font-medium text-label-secondary',
  head: 'flex items-center justify-between mb-3',
  name: 'text-sm font-semibold',
};

type TPhotoSessionsProps = {
  type:
    | 'photoSessions'
    | 'videoLooks'
    | 'bestItems'
    | 'videoStyles'
    | 'limited'
    | 'stickers'
    | 'aiVideos';
};

export const PhotoSessions: FC<TPhotoSessionsProps> = memo(({ type }) => {
  const t = useTranslations('MenuPage');
  const profile = useProfileContext()!;
  const { openPortal } = usePortalContext();
  const {
    genderMode,
    setPortalSeeAllMode,
    photoSessions,
    videoLooks,
    bestItems,
    videoStyles,
    limited,
    stickers,
    aiVideos,
    setPrevPortal,
    changeActiveStyle,
    styleCategories,
  } = useStyleContext();
  const sendGTM = useGTMContext();
  const vitrinaPosition = useVitrinaPosition(type);

  const items = useMemo(() => {
    switch (type) {
      case 'photoSessions':
        return photoSessions;
      case 'videoLooks':
        return videoLooks;
      case 'bestItems':
        return bestItems;
      case 'videoStyles':
        return videoStyles;
      case 'limited':
        return limited;
      case 'stickers':
        return stickers;
      case 'aiVideos':
        return aiVideos;
      default:
        return photoSessions;
    }
  }, [
    bestItems,
    photoSessions,
    type,
    videoLooks,
    videoStyles,
    limited,
    stickers,
    aiVideos,
  ]);

  const title = useMemo(() => {
    switch (type) {
      case 'photoSessions':
        return t('photoshootTitle');
      case 'videoLooks':
        return t('videoLooksTitle');
      case 'bestItems':
        return t('bestTitle');
      case 'videoStyles':
        return t('animatePhotoTitle');
      case 'limited':
        return t('limitedTitle');
      case 'stickers':
        return t('stickersTitle');
      case 'aiVideos':
        return t('videoTitle');
      default:
        return null;
    }
  }, [type, t]);

  const handleClickSeeAll = () => {
    setPrevPortal('main');
    setPortalSeeAllMode(type);
    openPortal(PORTALS.SeeAllImages);
  };

  const handleClickImage = (event: SyntheticEvent) => {
    const styleId = event.currentTarget.getAttribute('data-style-id');
    const currentIndex = event.currentTarget.getAttribute('data-style-ind');
    let position;

    if (styleId) {
      changeActiveStyle(styleId, type);
    }

    if (currentIndex) {
      position = Number(currentIndex);
    }

    const currentStyle = items?.find((item) => item.id === styleId);

    let currentCategory;

    if (currentStyle?.__typename! === 'Style') {
      currentCategory = _.find(styleCategories, (category) =>
        _.find(category.styles, { id: currentStyle?.id }),
      ) as StyleCategory | undefined;
    }

    sendGTM({
      event: 'menu_selection',
      type: currentStyle?.__typename!,
      block: getBlockName(type),
      category_name:
        currentStyle?.__typename! === 'Style'
          ? currentCategory?.name['en']
          : null,
      name: (currentStyle as TActualItem)?.name['en'],
      category_position: position,
      vitrina_position: vitrinaPosition,
      gender: genderMode,
    });

    setPrevPortal('main');
    setPortalSeeAllMode(type);
    openPortal(PORTALS.GenerateImage);
  };

  if (!items.length) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.line}>
        <div className={styles.lineInner}>
          {type === 'bestItems' ? (
            <BestIcon className={styles.lineIcon} />
          ) : type === 'aiVideos' ? (
            <AiVideosIcon className={styles.lineIcon} />
          ) : null}
          <div className={styles.lineTitle}>{title}</div>
          {type === 'photoSessions' && (
            <div className={styles.lineCount}>{t('photoshootPhotosCount')}</div>
          )}
        </div>
        {type !== 'limited' && <SeeAllButton onClick={handleClickSeeAll} />}
      </div>
      <BoxScrolled direction="horizontal" hideScrollbar gap={8} offsetOut={20}>
        {items?.map((item, i) => (
          <PackPreview
            key={item.id}
            id={item.id}
            index={i}
            isPopular={item.isPopular}
            isNew={item.isNew}
            name={item.name[profile?.lang]}
            image={getImageByGender(item, genderMode) ?? ''}
            handleClickImage={handleClickImage}
            isVideo={
              ['VideoLook', 'VideoStyle', 'CombiVideo'].includes(
                item.__typename!,
              ) ||
              getImageByGender(item, genderMode)?.includes('.mp4') ||
              false
            }
            isBest={type === 'bestItems'}
            isLimited={type === 'limited'}
            limitedUntil={item.limitedUntil}
            isActual={type === 'stickers'}
            __typename={item.__typename}
          />
        ))}
      </BoxScrolled>
    </div>
  );
});

PhotoSessions.displayName = 'PhotoSessions';
